import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import Sparkline from './Sparkline';
import SupplierAnnualChart from './SupplierAnnualChart';
import {
  BookOpenIcon,
  PencilIcon,
  Trash2Icon,
  SearchIcon,
  XIcon,
  Loader2Icon,
  PhoneIcon,
  MailIcon,
  HashIcon,
  MapPinIcon,
  FileTextIcon,
  ListIcon,
  TrendingUpIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTRY = (n) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

/**
 * SuppliersDirectory — Solo "İşletmeler" page.
 *
 * Lists every Firma / Yer the user has entered in their expenses, enriched
 * with aggregate stats (kullanım sayısı, toplam, ortalama, son kullanım)
 * and editable detail fields (adres, vergi no, telefon, e-posta, notlar).
 * Data comes from a single endpoint `/api/solo/suppliers` which already
 * tenant-scopes via company_id; this page just renders + edits via PUT.
 */
const SuppliersDirectory = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  // Map of normalized supplier key → 12-month TRY totals for the current
  // calendar year. Fed by /api/solo/suppliers/monthly so the sparklines and
  // annual chart share a single fetch.
  const [monthlyByKey, setMonthlyByKey] = useState({});
  const currentYear = new Date().getFullYear();
  const [chartSupplier, setChartSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // current row open in side panel
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [listRes, monthlyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/solo/suppliers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BACKEND_URL}/api/solo/suppliers/monthly?year=${currentYear}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setSuppliers(listRes.data?.suppliers || []);
      const lookup = {};
      for (const row of monthlyRes.data?.suppliers || []) {
        if (row.key) lookup[row.key] = row.months || [];
      }
      setMonthlyByKey(lookup);
    } catch (e) {
      toast.error('İşletme listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return suppliers;
    const q = query.trim().toLocaleLowerCase('tr-TR');
    return suppliers.filter((s) =>
      (s.name || '').toLocaleLowerCase('tr-TR').includes(q),
    );
  }, [suppliers, query]);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/solo/suppliers/${editing.id}`,
        {
          display_name: editing.name,
          address: editing.address || '',
          tax_number: editing.tax_number || '',
          phone: editing.phone || '',
          email: editing.email || '',
          notes: editing.notes || '',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Firma güncellendi');
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`${row.name} kaydını silmek istediğinize emin misiniz?`);
    if (!ok) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/solo/suppliers/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Firma silindi');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto" data-testid="suppliers-directory">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <BookOpenIcon className="w-5 h-5 text-purple-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">İşletmeler</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Harcamalarınıza eklediğiniz tüm Firma / Yer bilgileri tek listede.
            Detay alanları (adres, vergi no, telefon, not) ekleyince Yeni
            Harcama akışında otomatik dolar.
          </p>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Firma adı ara..."
            className="pl-9"
            data-testid="suppliers-search"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400 flex items-center justify-center gap-2">
            <Loader2Icon className="w-4 h-4 animate-spin" /> Yükleniyor...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            {query ? 'Aramanıza uyan kayıt yok.' : 'Henüz işletme kaydınız yok — Yeni Harcama eklediğinizde otomatik oluşturulur.'}
          </div>
        ) : (
          <>
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100" data-testid="suppliers-mobile-list">
            {filtered.map((s) => {
              const hasDetails = !!(s.address || s.tax_number || s.phone || s.email);
              const monthlyKey = (s.name || '').trim().toLocaleLowerCase('tr-TR');
              const monthsForYear = monthlyByKey[monthlyKey] || [];
              const hasYearData = monthsForYear.some((m) => m > 0);
              return (
                <div
                  key={s.id}
                  className="p-4"
                  data-testid={`supplier-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 break-words">{s.name}</span>
                        {s.is_recurring && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">
                            🔁 Tekrar
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {s.count}× kullanım • Son: {s.last_used || '—'}
                      </div>
                    </div>
                    {hasDetails ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 whitespace-nowrap">
                        ✓ Detay
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">— eksik —</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-gray-500">Toplam</div>
                      <div className="font-semibold text-gray-900 tabular-nums">{fmtTRY(s.total)}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-gray-500">Ortalama</div>
                      <div className="font-medium text-gray-700 tabular-nums">{fmtTRY(s.avg)}</div>
                    </div>
                  </div>
                  {hasYearData && (
                    <button
                      type="button"
                      onClick={() => setChartSupplier(s)}
                      className="w-full flex items-center justify-between mb-3 text-xs text-purple-700 bg-purple-50 rounded px-2 py-1.5"
                    >
                      <span>{currentYear} aylık trend</span>
                      <div className="flex items-center gap-1">
                        <Sparkline months={monthsForYear} />
                        <TrendingUpIcon className="w-3.5 h-3.5" />
                      </div>
                    </button>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {s.count > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="flex-1 min-w-[110px]"
                        onClick={() =>
                          navigate(`/dashboard/expenses?supplier=${encodeURIComponent(s.name)}`)
                        }
                        data-testid={`supplier-view-expenses-mobile-${s.id}`}
                      >
                        <ListIcon className="w-3.5 h-3.5 mr-1" /> Harcamalar
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="flex-1 min-w-[100px]"
                      onClick={() => setEditing({ ...s })}
                      data-testid={`supplier-edit-mobile-${s.id}`}
                    >
                      <PencilIcon className="w-3.5 h-3.5 mr-1" /> Düzenle
                    </Button>
                    {s.count === 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(s)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`supplier-delete-mobile-${s.id}`}
                      >
                        <Trash2Icon className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Firma / Yer</th>
                  <th className="px-4 py-3 font-medium text-right">Kullanım</th>
                  <th className="px-4 py-3 font-medium text-right">Toplam Harcama</th>
                  <th className="px-4 py-3 font-medium text-right">Ortalama</th>
                  <th className="px-4 py-3 font-medium">{currentYear} Aylık Trend</th>
                  <th className="px-4 py-3 font-medium">Son Kullanım</th>
                  <th className="px-4 py-3 font-medium">Detay</th>
                  <th className="px-4 py-3 font-medium text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const hasDetails = !!(s.address || s.tax_number || s.phone || s.email);
                  const monthlyKey = (s.name || '').trim().toLocaleLowerCase('tr-TR');
                  const monthsForYear = monthlyByKey[monthlyKey] || [];
                  const hasYearData = monthsForYear.some((m) => m > 0);
                  return (
                    <tr
                      key={s.id}
                      className="border-t hover:bg-purple-50/40"
                      data-testid={`supplier-row-${s.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{s.name}</span>
                          {s.is_recurring && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium"
                              title={`Son 12 ayda ${s.distinct_months_12mo} farklı ayda kayıt — düzenli ödeme gibi görünüyor`}
                              data-testid={`supplier-recurring-${s.id}`}
                            >
                              🔁 Tekrarlayan
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">{s.count}×</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-900">{fmtTRY(s.total)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600">{fmtTRY(s.avg)}</td>
                      <td className="px-4 py-3">
                        {hasYearData ? (
                          <button
                            type="button"
                            onClick={() => setChartSupplier(s)}
                            className="inline-flex items-center gap-2 hover:opacity-80"
                            data-testid={`supplier-trend-${s.id}`}
                            title={`${s.name} — ${currentYear} aylık dağılım`}
                          >
                            <Sparkline months={monthsForYear} />
                            <TrendingUpIcon className="w-3.5 h-3.5 text-purple-600" />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">— {currentYear} kayıt yok —</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{s.last_used || '—'}</td>
                      <td className="px-4 py-3">
                        {hasDetails ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                            ✓ Detay var
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">— eksik —</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {s.count > 0 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigate(
                                  `/dashboard/expenses?supplier=${encodeURIComponent(s.name)}`,
                                )
                              }
                              data-testid={`supplier-view-expenses-${s.id}`}
                              title="Bu firmanın harcamalarını gör"
                            >
                              <ListIcon className="w-3.5 h-3.5 mr-1" /> Harcamalar
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setEditing({ ...s })}
                            data-testid={`supplier-edit-${s.id}`}
                          >
                            <PencilIcon className="w-3.5 h-3.5 mr-1" /> Düzenle
                          </Button>
                          {s.count === 0 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(s)}
                              data-testid={`supplier-delete-${s.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2Icon className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>

      {/* Edit side panel */}
      {editing && (
        <div
          className="fixed inset-0 z-40 bg-black/30 flex justify-end"
          onClick={() => setEditing(null)}
          data-testid="supplier-edit-panel"
        >
          <div
            className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Firma Düzenle</h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="supplier-edit-close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Firma / Yer Adı
                </label>
                <Input
                  value={editing.name || ''}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="supplier-edit-name"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Değişiklikleri kaydederseniz, geçmiş harcama kayıtlarındaki firma adı da güncellenir.
                </p>
              </div>
              <div>
                <label className=" text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  <MapPinIcon className="w-3.5 h-3.5" /> Adres
                </label>
                <Textarea
                  rows={2}
                  value={editing.address || ''}
                  onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                  data-testid="supplier-edit-address"
                />
              </div>
              <div>
                <label className=" text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  <HashIcon className="w-3.5 h-3.5" /> Vergi No
                </label>
                <Input
                  value={editing.tax_number || ''}
                  onChange={(e) => setEditing({ ...editing, tax_number: e.target.value })}
                  data-testid="supplier-edit-tax-number"
                />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className=" text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <PhoneIcon className="w-3.5 h-3.5" /> Telefon
                  </label>
                  <Input
                    value={editing.phone || ''}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                    data-testid="supplier-edit-phone"
                  />
                </div>
                <div>
                  <label className=" text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <MailIcon className="w-3.5 h-3.5" /> E-posta
                  </label>
                  <Input
                    type="email"
                    value={editing.email || ''}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    data-testid="supplier-edit-email"
                  />
                </div>
              </div>
              <div>
                <label className=" text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                  <FileTextIcon className="w-3.5 h-3.5" /> Notlar
                </label>
                <Textarea
                  rows={3}
                  value={editing.notes || ''}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="supplier-edit-notes"
                />
              </div>

              <div className="border-t pt-3 text-xs text-gray-500 space-y-1">
                <div>Kullanım: <strong className="text-gray-700">{editing.count}×</strong></div>
                <div>Toplam: <strong className="text-gray-700">{fmtTRY(editing.total)}</strong></div>
                <div>Son: <strong className="text-gray-700">{editing.last_used || '—'}</strong></div>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2 bg-gray-50">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                İptal
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="supplier-edit-save"
              >
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Annual trend chart modal */}
      {chartSupplier && (
        <SupplierAnnualChart
          supplier={chartSupplier}
          onClose={() => setChartSupplier(null)}
          initialYear={currentYear}
        />
      )}
    </div>
  );
};

export default SuppliersDirectory;
